// Central localStorage-backed data store for DHO
// Products, Blog Posts, Cart, and Settings

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  category: string;
  badge?: string;
  imageUrl?: string;
  inStock: boolean;
  featured: boolean;
  createdAt: number;
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  imageUrl?: string;
  author: string;
  published: boolean;
  publishedAt: number;
  createdAt: number;
}

export interface CartItem {
  productId: string;
  quantity: number;
}

export interface StoreSettings {
  adminPasswordHash: string; // SHA-256 hex of password
  paypalClientId: string;
  paypalMode: 'sandbox' | 'live';
  squareAppId: string;
  squareLocationId: string;
  squareMode: 'sandbox' | 'production';
  checkoutProvider: 'paypal' | 'square' | 'both';
  storeName: string;
  storeEmail: string;
  storeCurrency: string;
}

// --- Storage keys ---
const PRODUCTS_KEY = 'dho:products';
const POSTS_KEY = 'dho:posts';
const CART_KEY = 'dho:cart';
const SETTINGS_KEY = 'dho:settings';
const ADMIN_SESSION_KEY = 'dho:admin-session';

// --- Default seed data ---
const SEED_PRODUCTS: Product[] = [
  {
    id: 'p1', name: 'Dakota Ridge Hunting Vest', description: 'Premium camo hunting vest with 8 pockets, blaze orange panel, and windproof shell.', price: 189.99, category: 'apparel', badge: 'Best Seller', imageUrl: '', inStock: true, featured: true, createdAt: Date.now(),
  },
  {
    id: 'p2', name: 'ProSeries Compound Bow 70lb', description: '70lb draw weight, 320fps IBO speed, 7" brace height. Includes sight, rest, and quiver.', price: 649.99, category: 'archery', badge: 'New Arrival', imageUrl: '', inStock: true, featured: true, createdAt: Date.now(),
  },
  {
    id: 'p3', name: 'All-Weather Camo Jacket', description: 'Waterproof 3-in-1 system with removable fleece liner. Mossy Oak® Break-Up Country pattern.', price: 219.99, category: 'apparel', badge: 'Staff Pick', imageUrl: '', inStock: true, featured: true, createdAt: Date.now(),
  },
  {
    id: 'p4', name: 'Heavy-Duty Pack Frame 85L', description: 'Aircraft-grade aluminum frame, 85L capacity, hip-belt suspension. Built for backcountry elk hunts.', price: 329.99, category: 'camping', badge: 'Top Rated', imageUrl: '', inStock: true, featured: true, createdAt: Date.now(),
  },
  {
    id: 'p5', name: 'Dakota Scope 3-9x40mm', description: 'Multi-coated optics, fast-focus eyepiece, waterproof/fogproof. Zero-reset turrets.', price: 289.99, category: 'hunting', badge: 'Best Seller', imageUrl: '', inStock: true, featured: false, createdAt: Date.now(),
  },
  {
    id: 'p6', name: 'Walleye Spinning Combo 6\'6"', description: '2-piece medium-light rod with smooth 10-bearing spinning reel. Perfect for Dakota walleye.', price: 149.99, category: 'fishing', badge: 'Best Seller', imageUrl: '', inStock: true, featured: false, createdAt: Date.now(),
  },
  {
    id: 'p7', name: 'Whitetail Ground Blind XL', description: 'Hub-style quick setup in 60 seconds. 4-person capacity with full 360° shooting lanes.', price: 179.99, category: 'hunting', imageUrl: '', inStock: true, featured: false, createdAt: Date.now(),
  },
  {
    id: 'p8', name: 'Trail Camera 24MP Cellular', description: 'LTE-enabled, sends HD photos to your phone. 90ft detection range, 0.3s trigger speed.', price: 179.99, category: 'accessories', badge: 'Best Seller', imageUrl: '', inStock: true, featured: false, createdAt: Date.now(),
  },
];

const SEED_POSTS: BlogPost[] = [
  {
    id: 'b1',
    title: 'Top 5 Tips for Early Season Whitetail Hunting',
    slug: 'top-5-tips-early-season-whitetail',
    excerpt: 'Beat the summer heat and tag that velvet buck with these field-tested strategies from our pro staff.',
    content: `Early season whitetail hunting is all about playing it cool — literally. Deer are still in summer patterns, and the heat means you need to be extra careful about scent control and timing.\n\n**1. Hunt the Thermals**\nMorning thermals carry your scent upward as the day warms. Set up downwind of bedding areas before first light.\n\n**2. Focus on Food Sources**\nCorn, soybeans, and clover are the magnets in early season. Find the food, find the deer.\n\n**3. Scent Control is King**\nShower with scent-free soap, store your clothes in sealed bags with dirt and leaves from your hunting area.\n\n**4. Keep Intrusion Low**\nEarly season bucks are still patternable. One blown encounter can shift them to nocturnal.\n\n**5. Glass Before You Walk**\nUse optics from a distance to pattern deer before committing to a stand location.`,
    imageUrl: '',
    author: 'Dakota Horizon Pro Staff',
    published: true,
    publishedAt: Date.now() - 7 * 24 * 60 * 60 * 1000,
    createdAt: Date.now() - 7 * 24 * 60 * 60 * 1000,
  },
  {
    id: 'b2',
    title: 'Best Ice Fishing Spots in the Dakotas for 2026',
    slug: 'best-ice-fishing-dakotas-2026',
    excerpt: 'Our guide to the top walleye and perch lakes across North and South Dakota this ice season.',
    content: `The Dakotas are a winter fishing paradise. When the ice locks up, the walleye bite turns on in a big way.\n\n**Lake Oahe**\nThis massive Missouri River reservoir consistently produces trophy walleye through the ice. Target the 15-25ft range near creek channels.\n\n**Devils Lake**\nThe perch fishing at Devils Lake is world-class. Find structure and you'll find fish stacked up.\n\n**Lake Sakakawea**\nNorth Dakota's big water delivers both walleye and northern pike. Use tip-ups for the pike and jigging spoons for eyes.\n\n**Gear Tips**\nMake sure your auger is sharp, your flasher is tuned, and your rod is sensitive. A 26" medium-light ice rod with 6lb fluorocarbon is our go-to setup.`,
    imageUrl: '',
    author: 'Team DHO',
    published: true,
    publishedAt: Date.now() - 3 * 24 * 60 * 60 * 1000,
    createdAt: Date.now() - 3 * 24 * 60 * 60 * 1000,
  },
  {
    id: 'b3',
    title: 'Gear Review: ProSeries Compound Bow 70lb',
    slug: 'review-proseries-compound-bow-70lb',
    excerpt: 'We put our flagship bow through a full season of field testing. Here\'s the honest verdict.',
    content: `After a full archery season with the ProSeries 70lb compound, here's our take.\n\n**Accuracy**\nOut-of-the-box accuracy was impressive. We were shooting consistent 3" groups at 40 yards after just one paper-tuning session.\n\n**Speed**\nAt a true 315fps with a 400-grain arrow, it's fast enough for any North American big game application.\n\n**Feel**\nThe grip is slim and the draw cycle is smooth. Back wall is solid with minimal jump at the shot.\n\n**Verdict**\nFor the price point, this bow punches well above its weight class. It's our most recommended bow for hunters stepping up from a beginner setup.`,
    imageUrl: '',
    author: 'Marcus T., Pro Staff',
    published: true,
    publishedAt: Date.now() - 1 * 24 * 60 * 60 * 1000,
    createdAt: Date.now() - 1 * 24 * 60 * 60 * 1000,
  },
];

const DEFAULT_SETTINGS: StoreSettings = {
  adminPasswordHash: '', // set on first login
  paypalClientId: '',
  paypalMode: 'sandbox',
  squareAppId: '',
  squareLocationId: '',
  squareMode: 'sandbox',
  checkoutProvider: 'paypal',
  storeName: 'Dakota Horizon Outdoors',
  storeEmail: 'info@dakotahorizonoutdoors.com',
  storeCurrency: 'USD',
};

// --- SHA-256 helper ---
export async function sha256(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

// --- Products ---
export function getProducts(): Product[] {
  try {
    const raw = localStorage.getItem(PRODUCTS_KEY);
    if (!raw) {
      localStorage.setItem(PRODUCTS_KEY, JSON.stringify(SEED_PRODUCTS));
      return SEED_PRODUCTS;
    }
    return JSON.parse(raw) as Product[];
  } catch {
    return SEED_PRODUCTS;
  }
}

export function saveProducts(products: Product[]): void {
  localStorage.setItem(PRODUCTS_KEY, JSON.stringify(products));
}

export function addProduct(product: Omit<Product, 'id' | 'createdAt'>): Product {
  const products = getProducts();
  const newProduct: Product = {
    ...product,
    id: `p${Date.now()}`,
    createdAt: Date.now(),
  };
  saveProducts([...products, newProduct]);
  return newProduct;
}

export function updateProduct(id: string, updates: Partial<Product>): void {
  const products = getProducts();
  saveProducts(products.map((p) => (p.id === id ? { ...p, ...updates } : p)));
}

export function deleteProduct(id: string): void {
  const products = getProducts();
  saveProducts(products.filter((p) => p.id !== id));
}

// --- Blog Posts ---
export function getPosts(): BlogPost[] {
  try {
    const raw = localStorage.getItem(POSTS_KEY);
    if (!raw) {
      localStorage.setItem(POSTS_KEY, JSON.stringify(SEED_POSTS));
      return SEED_POSTS;
    }
    return JSON.parse(raw) as BlogPost[];
  } catch {
    return SEED_POSTS;
  }
}

export function savePosts(posts: BlogPost[]): void {
  localStorage.setItem(POSTS_KEY, JSON.stringify(posts));
}

export function addPost(post: Omit<BlogPost, 'id' | 'createdAt'>): BlogPost {
  const posts = getPosts();
  const newPost: BlogPost = {
    ...post,
    id: `b${Date.now()}`,
    createdAt: Date.now(),
  };
  savePosts([...posts, newPost]);
  return newPost;
}

export function updatePost(id: string, updates: Partial<BlogPost>): void {
  const posts = getPosts();
  savePosts(posts.map((p) => (p.id === id ? { ...p, ...updates } : p)));
}

export function deletePost(id: string): void {
  const posts = getPosts();
  savePosts(posts.filter((p) => p.id !== id));
}

// --- Cart ---
export function getCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    return raw ? (JSON.parse(raw) as CartItem[]) : [];
  } catch {
    return [];
  }
}

export function saveCart(cart: CartItem[]): void {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
}

// --- Settings ---
export function getSettings(): StoreSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<StoreSettings>) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function saveSettings(settings: Partial<StoreSettings>): void {
  const current = getSettings();
  localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ...current, ...settings }));
}

// --- Admin Session ---
export function isAdminLoggedIn(): boolean {
  return localStorage.getItem(ADMIN_SESSION_KEY) === 'true';
}

export function adminLogin(): void {
  localStorage.setItem(ADMIN_SESSION_KEY, 'true');
}

export function adminLogout(): void {
  localStorage.removeItem(ADMIN_SESSION_KEY);
}

// Default admin password is "DHO-Admin2024" — can be changed in settings
export const DEFAULT_ADMIN_PASSWORD = 'DHO-Admin2024';
